import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useAxios from "../../../../api/hooks/useAxios";
import useColors from "../../../../api/hooks/useColors";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";

const RefreshEntitiesCacheModal = ({ open, handleClose }) => {
  const colors = useColors();
  const {
    refreshEntitiesCacheDB,
    refreshEntitiesCacheRouter,
    getCustomerList,
  } = useAPI();
  const axiosWithAuth0 = useAxios();

  const [customerIds, setCustomerIds] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [allCustomers, setAllCustomers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (open) {
      getCustomerList()
        .then((response) => {
          const baseCustomers = response.data;
          const supportCustomers = baseCustomers.map((id) => `support.${id}`);
          setCustomerList([...baseCustomers, ...supportCustomers]);
        })
        .catch((error) => {
          Toast.error("Failed to fetch customer list");
        });
    }
  }, [open]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleRefreshCache = async () => {
    setLoading(true);
    clearMessages();

    try {
      let targetCustomerId;
      if (allCustomers) {
        targetCustomerId = "all";
      } else if (customerIds.length > 0) {
        targetCustomerId = customerIds.join(",");
      } else {
        setError(
          "Please select at least one customer ID or select 'All Customers'"
        );
        setLoading(false);
        return;
      }

      const baseUrl = axiosWithAuth0.defaults.baseURL;

      const dbResponse = await refreshEntitiesCacheDB(targetCustomerId);
      const routerResponse = await refreshEntitiesCacheRouter(
        targetCustomerId,
        baseUrl
      );

      const dbSuccessful = dbResponse.data.successful_customers || [];
      const dbFailed = dbResponse.data.failed_customers || [];
      const routerSuccessful = routerResponse.data.successful_customers || [];
      const routerFailed = routerResponse.data.failed_customers || [];

      const allSuccessful = dbSuccessful.filter((c) =>
        routerSuccessful.includes(c)
      );
      const allFailed = [...new Set([...dbFailed, ...routerFailed])];
      const partialSuccess = [
        ...dbSuccessful.filter((c) => !routerSuccessful.includes(c)),
        ...routerSuccessful.filter((c) => !dbSuccessful.includes(c)),
      ];

      let message = "";

      if (allSuccessful.length > 0) {
        message += `Successfully refreshed cache for ${
          allSuccessful.length
        } customer(s): ${allSuccessful.join(", ")}`;
      }

      if (partialSuccess.length > 0) {
        if (message) message += ". ";
        message += `Partial success (one operation failed) for: ${partialSuccess.join(
          ", "
        )}`;
      }

      if (allFailed.length > 0) {
        if (message) message += ". ";
        message += `Failed: ${allFailed.join(", ")}`;
      }

      if (!message) {
        message = "No customers processed";
      }

      setSuccess(message);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to refresh cache");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setCustomerIds([]);
    setAllCustomers(false);
    clearMessages();
    handleClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading && !isAutocompleteOpen) {
      handleRefreshCache();
    }
  };

  const handleAutocompleteKeyDown = (event) => {
    if (event.key === "Enter" && isAutocompleteOpen) {
      event.stopPropagation();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      onKeyDown={handleKeyPress}
    >
      <DialogTitle>
        <Typography variant="h6">Refresh Entities Cache</Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              ref={autocompleteRef}
              multiple
              freeSolo
              filterSelectedOptions
              options={customerList}
              value={customerIds}
              onChange={(_, newValue) => setCustomerIds(newValue)}
              disabled={allCustomers || loading}
              selectOnFocus
              onOpen={() => setIsAutocompleteOpen(true)}
              onClose={() => setIsAutocompleteOpen(false)}
              onInputKeyDown={handleAutocompleteKeyDown}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Customer IDs"
                  placeholder="Select or type customer IDs"
                  helperText="Select multiple customers or type custom IDs (support. prefixed customers are available)"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allCustomers}
                  onChange={(e) => setAllCustomers(e.target.checked)}
                  disabled={loading}
                />
              }
              label="All Customers (WARNING: This will refresh cache for ALL customers)"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              This will refresh both the DB cache (clears and re-populates from
              database) and the router cache (pre-warms by simulating customer
              requests for all entity types).
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleRefreshCache}
              disabled={loading || (customerIds.length === 0 && !allCustomers)}
              startIcon={loading ? <LoadingIcon size={16} /> : null}
              sx={{ backgroundColor: colors.primary_main }}
            >
              Refresh Entities Cache
            </Button>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseModal} disabled={loading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefreshEntitiesCacheModal;
